import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { getWorkspaceSettings, getWorkspaceSettingsStudent } from "../../api/workspaceSettings";
import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import {
  MembersCard,
  IndividualWorkspaceMembersCard,
  ReservationsStatusCard,
  StorageUsageCard,
  UserStatusCard,
  WorkspaceInfoCard,
  StudentUserStatusCard,
  StudentReservationsStatusCard,
  WorkspaceBlockingCard,
} from "../../components/settings";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import GenericLoader from "@/components/GenericLoader.tsx";
import useUserStore from "@/features/auth/store/useUserStore";

export default function WorkspaceSettingsPage() {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { settings, isLoading, error, setSettings, setLoading, setError } =
    useWorkspaceSettingsStore();
  const { user } = useUserStore();
  const isStudent = user?.role === "student";

  const fetchSettings = async () => {
    if (!workspaceId) return;

    setLoading(true);
    try {
      const response = isStudent
        ? await getWorkspaceSettingsStudent(workspaceId)
        : await getWorkspaceSettings(workspaceId);

      setSettings(response.data.data);
    } catch (error) {
      console.error("Failed to fetch workspace settings:", error);
      setError(
        t("workspaces:settings.errors.fetchFailed", {
          defaultValue: "Failed to load workspace settings",
        }),
      );
      toast.error(
        t("workspaces:settings.errors.fetchFailed", {
          defaultValue: "Failed to load workspace settings",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [workspaceId, isStudent]);

  if (isLoading) {
    return <GenericLoader />;
  }

  if (error || !settings) {
    return (
      <div className="flex h-[calc(100dvh_-_4rem)] items-center justify-center">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
        >
          <div className="text-destructive bg-destructive/10 mx-auto flex h-10 w-10 items-center justify-center rounded-full">
            <span className="text-xl">⚠️</span>
          </div>
          <p className="text-accent-foreground mt-4 text-base font-medium">{t("common:error")}</p>
          <p className="text-muted-foreground mt-1 text-sm">
            {error ||
              t("workspaces:settings.errors.noSettings", { defaultValue: "No settings available" })}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh_-_4rem)]">
      <title>{`${t("workspaces:settings.title", { defaultValue: "Workspace Settings" })} - Ximly`}</title>
      <ScrollArea className="h-full">
        <div className="container mx-auto max-w-4xl px-6 py-8">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-accent-foreground text-2xl font-bold">
              {t("workspaces:settings.title", { defaultValue: "Workspace Settings" })}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {t("workspaces:settings.subtitle", {
                defaultValue: "Manage your workspace settings and preferences",
              })}
            </p>
          </motion.div>

          <div className="space-y-6">
            {/* Workspace Information Section */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
                {t("workspaces:settings.sections.workspaceInfo", {
                  defaultValue: "Workspace Information",
                })}
              </div>
              <div className="space-y-3">
                <WorkspaceInfoCard />
                <StorageUsageCard />
                {isStudent && <WorkspaceBlockingCard />}
              </div>
            </motion.div>

            <Separator className="bg-border/30" />

            {/* User Status Section */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
                {t("workspaces:settings.sections.userStatus", { defaultValue: "Your Status" })}
              </div>
              <div className="space-y-3">
                {isStudent ? <StudentUserStatusCard /> : <UserStatusCard />}
                {isStudent ? <StudentReservationsStatusCard /> : <ReservationsStatusCard />}
              </div>
            </motion.div>

            <Separator className="bg-border/30" />

            {/* Members Section */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="text-muted-foreground mb-4 text-xs tracking-wider uppercase">
                {t("workspaces:settings.sections.members", { defaultValue: "Members" })}
              </div>
              <div className="space-y-3">
                {settings.workspace.workspace_type === "personal" ? (
                  <IndividualWorkspaceMembersCard />
                ) : (
                  <MembersCard />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
